import React, { useState } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

/** Reusable Comment Section for Stories, Prayers, Goals */
export function CommentSection({ itemType, itemId, token }) {
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const endpointMap = {
    story: `${API}/api/stories/${itemId}/comments`,
    prayer: `${API}/api/prayer-requests/${itemId}/comments`,
    goal: `${API}/api/career-goals/${itemId}/comments`,
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(endpointMap[itemType], { headers: { Authorization: `Bearer ${token}` } });
      setComments(res.data);
    } catch { /* ignore */ }
  };

  const handleToggle = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const handlePost = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(endpointMap[itemType], null, {
        params: { content: newComment },
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments([...comments, res.data]);
      setNewComment('');
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        onClick={handleToggle}
        className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-1"
        data-testid={`toggle-comments-${itemId}`}
      >
        <MessageCircle className="h-3.5 w-3.5" />
        {showComments ? 'Hide Comments' : `Comments${comments.length > 0 ? ` (${comments.length})` : ''}`}
      </button>
      {showComments && (
        <div className="mt-2 space-y-2">
          {comments.length > 0 ? comments.map(c => (
            <div key={c.comment_id} className="bg-slate-900/50 rounded-lg px-3 py-2">
              <p className="text-slate-300 text-sm">{c.content}</p>
              <p className="text-xs text-slate-500 mt-1">{c.user_name} — {new Date(c.created_at).toLocaleDateString()}</p>
            </div>
          )) : (
            <p className="text-xs text-slate-500 italic">No comments yet</p>
          )}
          <div className="flex gap-2 mt-2">
            <input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
              data-testid={`comment-input-${itemId}`}
            />
            <Button size="sm" onClick={handlePost} disabled={posting || !newComment.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid={`post-comment-${itemId}`}>
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
